import { useMutation, useQuery } from "@tanstack/react-query"
import { useState } from "react"
import { Link, useSearchParams } from "react-router-dom"
import GameScreenLayout from "@/components/GameScreenLayout"
import GeoPointsList from "@/components/geo/GeoPointsList"
import { normalizeGeoPoint } from "@/components/geo/geoPointHelpers"
import { usePlayerGpsState } from "@/hooks/usePlayerGpsState"
import {
  acceptMailContract,
  claimMailContract,
  getOpenMailContracts,
  getPostOfficePoints,
} from "@/api/gameApi"
import { getGameApiErrorMessage } from "@/api/gameApiErrors"
import { ELEMENT_NAMES } from "@/constants/guardians"
import { useGuardiansPassport } from "@/features/passport/GuardiansPassportContext"

export default function PostOfficePage() {
  const [params] = useSearchParams()
  const highlightId = params.get("pointId")
  const gps = usePlayerGpsState()
  const [postPointId, setPostPointId] = useState<string | null>(highlightId)
  const { collectionCards, syncGameState, bumpCards } = useGuardiansPassport()

  const pointsQuery = useQuery({
    queryKey: ["post-offices"],
    queryFn: getPostOfficePoints,
  })

  const contractsQuery = useQuery({
    queryKey: ["mail-contracts-open"],
    queryFn: () => getOpenMailContracts({}),
    refetchInterval: 20_000,
  })

  const accept = useMutation({
    mutationFn: ({
      contractId,
      counterCardId,
      pointId,
    }: {
      contractId: string
      counterCardId: string
      pointId: string
    }) => {
      if (!gps) throw new Error("Нужен GPS")
      return acceptMailContract(contractId, {
        pointId,
        playerLat: gps.lat,
        playerLng: gps.lng,
        counterCardId,
      })
    },
    onSuccess: async () => {
      await syncGameState()
      bumpCards()
      void contractsQuery.refetch()
    },
  })

  const claim = useMutation({
    mutationFn: ({ contractId, pointId }: { contractId: string; pointId: string }) => {
      if (!gps) throw new Error("Нужен GPS")
      return claimMailContract(contractId, {
        pointId,
        playerLat: gps.lat,
        playerLng: gps.lng,
      })
    },
    onSuccess: async () => {
      await syncGameState()
      bumpCards()
      void contractsQuery.refetch()
    },
  })

  const points = (pointsQuery.data ?? []).map((p) =>
    normalizeGeoPoint({
      id: p.id,
      name: p.name,
      lat: p.lat,
      lng: p.lng,
      radiusM: p.radiusM,
    })
  )

  const contracts = contractsQuery.data ?? []

  return (
    <GameScreenLayout title="Почта" backTo="/play">
      <section className="mb-4">
        <h2 className="mb-2 text-sm font-medium">Отделения</h2>
        {!postPointId ? (
          <p className="mb-2 text-xs text-amber-200/90">Выберите отделение для обмена.</p>
        ) : (
          <p className="mb-2 text-xs text-guardians-accent">Активное: {postPointId}</p>
        )}
        {postPointId ? (
          <Link
            to={`/play/post-office/create-lot?pointId=${encodeURIComponent(postPointId)}`}
            className="mb-2 inline-block text-xs text-guardians-accent underline"
          >
            Создать лот обмена
          </Link>
        ) : null}
        <GeoPointsList
          points={points}
          gps={gps}
          highlightId={postPointId ?? highlightId}
          renderActions={({ point, inRange }) => (
            <button
              type="button"
              disabled={!inRange}
              onClick={() => setPostPointId(point.id)}
              className="rounded-lg border border-white/20 px-3 py-1.5 text-xs disabled:opacity-40"
            >
              Здесь
            </button>
          )}
        />
      </section>

      <section>
        <h2 className="mb-2 text-sm font-medium">Открытые лоты</h2>
        {contractsQuery.isLoading ? (
          <p className="text-sm text-guardians-muted">Загрузка…</p>
        ) : contracts.length === 0 ? (
          <p className="text-sm text-guardians-muted">Нет лотов</p>
        ) : (
          <ul className="space-y-3">
            {contracts.map((c) => (
              <li key={c.id} className="rounded-xl border border-white/10 p-3 text-sm">
                <p className="font-medium">{c.roomCode}</p>
                <p className="text-xs text-guardians-muted">
                  Статус: {c.status} · хочет {ELEMENT_NAMES[c.wantedElement]}{" "}
                  {c.wantedColorRank}
                </p>
                {c.status === "OPEN" && postPointId && gps ? (
                  <select
                    className="mt-2 w-full rounded-lg border border-white/10 bg-black/30 px-2 py-1 text-xs"
                    defaultValue=""
                    onChange={(e) => {
                      const cardId = e.target.value
                      if (!cardId) return
                      accept.mutate({
                        contractId: c.id,
                        counterCardId: cardId,
                        pointId: postPointId,
                      })
                      e.target.value = ""
                    }}
                  >
                    <option value="">Принять картой…</option>
                    {collectionCards.map((card) => (
                      <option key={card.id} value={card.id}>
                        {card.element} {card.colorRank} Lv.{card.level}
                      </option>
                    ))}
                  </select>
                ) : null}
                {c.status === "READY_TO_CLAIM" && postPointId && gps ? (
                  <button
                    type="button"
                    className="mt-2 rounded-lg bg-guardians-accent px-3 py-1 text-xs text-guardians-bg"
                    onClick={() =>
                      claim.mutate({ contractId: c.id, pointId: postPointId })
                    }
                  >
                    Забрать
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>

      {(accept.isError || claim.isError) && (
        <p className="mt-3 text-sm text-red-300">
          {getGameApiErrorMessage(accept.error ?? claim.error, "Ошибка почты")}
        </p>
      )}
    </GameScreenLayout>
  )
}
